<template>
  <ListItemsWithTableLayout>
    <template #body>
      <HandlerAccessDenied :is-denied="isDenied">
        <div ref="headerContainerRef" class="statistics-header-container">
          <div class="statistics-header">
            <h2 class="statistics-title">Статистика расходов</h2>
          </div>

          <StatisticsCards
            :statistics="statisticsStore.statistics"
            :is-loading="isItemsLoading"
          />

          <StatisticsFilters
            :applied-filter="itemsTableDisplaySettings.filter"
            @apply="applyFilter"
            @reset="resetFilter"
          />
        </div>

        <div
          id="table-container"
          class="table-container flex flex-col"
          :class="[
            statisticsStore.items && statisticsStore.items.length == 0
              ? 'empty'
              : 'filled',
          ]"
          :style="{ '--header-height': headerHeight + 'px' }"
        >
          <StatisticsItemsTable
            :items="statisticsStore.items"
            :isActiveFirst="false"
            :isLoading="isItemsLoading"
            :rowsCount="rowsCount(43) * itemsTableDisplaySettings.first"
            @sort-table="sortItemsTable"
          ></StatisticsItemsTable>
          <EmptyTableMessage
            v-if="
              statisticsStore.items &&
              statisticsStore.items.length == 0 &&
              !isItemsLoading
            "
          >
            <template #text> Нет данных для отображения. </template>
          </EmptyTableMessage>
        </div>
        <Paginator
          v-model:first="itemsTableDisplaySettings.first"
          @update:first="updateItemsList"
          :rows="1"
          class="s-paginator"
          :totalRecords="statisticsStore.itemsPagesCount"
          template="PrevPageLink CurrentPageReport NextPageLink"
          currentPageReportTemplate="{first} из {totalRecords}"
        />
      </HandlerAccessDenied>
    </template>
  </ListItemsWithTableLayout>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUpdated, onUnmounted } from "vue";
import { useAuthStore } from "@/store/auth/login";
import {
  useStatisticsStore,
  StatisticsParams,
  StatisticsFilter,
} from "@/store/expenses/statistics";
import { setStatisticsParamsByFilter } from "@/services/expenses/statistics";
import ListItemsWithTableLayout from "@/widgets/layouts/ListItemsWithTableLayout.vue";
import HandlerAccessDenied from "@/shared/base/HandlerAccessDenied.vue";
import StatisticsCards from "@/widgets/statistics/StatisticsCards.vue";
import StatisticsFilters from "@/widgets/statistics/StatisticsFilters.vue";
import StatisticsItemsTable from "@/entities/expenses/StatisticsItemsTable.vue";
import EmptyTableMessage from "@/entities/segments/EmptyTableMessage.vue";
import Paginator from "primevue/paginator";
import { rootGetters } from "@/main";
import { rowsCount } from "@/services/utils/Pagination";

const authStore = useAuthStore();
const statisticsStore = useStatisticsStore();

const headerContainerRef = ref<HTMLElement | null>(null);
const headerHeight = ref(350);

const isDenied = computed(() => {
  return authStore.isDeniedForPermissions([
    "expenses:read",
    "expenses:edit",
    "system:read",
    "system:edit",
  ]);
});

const isItemsLoading = ref(false);
const itemsTableDisplaySettings = ref({
  first: 0,
  orderField: "",
  filter: {
    brand: [] as string[],
    segment: [] as number[],
    ads_cabinet: [] as number[],
    date: null as any,
    state: [] as number[],
    platform: [] as string[],
  } as StatisticsFilter,
});

const applyFilter = (filter: StatisticsFilter) => {
  itemsTableDisplaySettings.value.filter = filter;
  itemsTableDisplaySettings.value.first = 0;
  updateItemsList();
};

const resetFilter = () => {
  itemsTableDisplaySettings.value.filter = {
    brand: [],
    segment: [],
    ads_cabinet: [],
    date: null,
    state: [],
    platform: [],
  };
  itemsTableDisplaySettings.value.first = 0;
  updateItemsList();
};

const sortItemsTable = (order_by: string) => {
  itemsTableDisplaySettings.value.orderField = order_by;
  updateItemsList();
};

const updateItemsList = () => {
  let isEnded = false;
  setTimeout(() => {
    if (!isEnded) {
      isItemsLoading.value = true;
    }
  }, 200);

  const calculatedPerPage = rowsCount(43);
  const params: StatisticsParams = {
    page: itemsTableDisplaySettings.value.first + 1,
    per_page: Math.min(calculatedPerPage, 100),
  };

  setStatisticsParamsByFilter(params, itemsTableDisplaySettings.value);

  statisticsStore.getSegmentCosts(params).finally(() => {
    isItemsLoading.value = false;
    isEnded = true;
  });
};

const updateHeaderHeight = () => {
  if (headerContainerRef.value) {
    headerHeight.value = headerContainerRef.value.offsetHeight;
  }
};

onMounted(() => {
  loadItemsData();
  updateHeaderHeight();
  window.addEventListener("resize", updateHeaderHeight);
});

onUpdated(() => {
  updateHeaderHeight();
});

onUnmounted(() => {
  window.removeEventListener("resize", updateHeaderHeight);
});

const loadItemsData = () => {
  isItemsLoading.value = true;
  updateItemsList();
};
</script>

<style lang="scss" scoped>
.statistics-container {
  padding: 20px;
}

.statistics-header {
  margin-bottom: 20px;
  margin-top: 13px;

  .statistics-title {
    margin: 0;
    font-size: 1.71rem;
    font-weight: 500;
    line-height: 130%;
    color: $main-font-color;
  }
}

.list-page-layout .table-container {
  position: relative;
  top: 20px;
  height: calc(100vh - 340px - 63px);
  max-height: calc(100vh - 340px - 63px);
  min-height: 200px;
  background-color: white;
  border: 1px solid #edecef;
  border-radius: 8px;
  padding: 20px;
}

:deep(.s-paginator) {
  position: relative;
  bottom: -20px;
}
</style>
